/**
 * DOSÉ — Section avis
 *
 * Une seule source de vérité pour le texte des avis : un Google Sheet.
 * Le thème ne sert qu'à enrichir (photos) ou corriger (n'importe quel champ)
 * un avis déjà présent dans la feuille, via des blocs reliés par leur `id`.
 *
 * Trois formats de source acceptés, détectés à la lecture :
 *   - la réponse `gviz` d'un Google Sheet partagé en lecture (voie normale) ;
 *   - un tableau JSON (script Google Apps Script publié en application web) ;
 *   - un CSV (feuille « publiée sur le Web »).
 */

const VOTE_STORE = 'dose-avis:votes';
const CACHE_PREFIX = 'dose-avis:cache:';

/* ------------------------------------------------------------------ outils */

/** Minuscules, sans accent, sans ponctuation : pour comparer des libellés. */
function norm(value) {
  return (value == null ? '' : String(value))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function escapeHtml(value) {
  return (value == null ? '' : String(value))
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Le texte d'un avis peut contenir des retours à la ligne, rien d'autre. */
function textToHtml(value) {
  return escapeHtml(value).replace(/\r?\n/g, '<br>');
}

/**
 * Empreinte courte et stable d'un avis, quand la colonne `id` est vide.
 * Déduite du contenu : elle survit à un tri ou à une insertion de ligne,
 * là où un numéro de rang ferait glisser les photos d'un avis à l'autre.
 */
function fingerprint(row) {
  const graine = [row.auteur, row.date, row.titre, row.avis].map((v) => (v || '').trim()).join('|');
  let h = 5381;
  for (let i = 0; i < graine.length; i += 1) h = ((h * 33) ^ graine.charCodeAt(i)) >>> 0;
  return 'avis-' + h.toString(36);
}

/**
 * Une note saisie à la française — « 4,7 » — doit valoir 4.7. Sans cette
 * conversion, Number('4,7') renvoie NaN et la rangée d'étoiles se vide.
 */
function toNote(value) {
  return Number(String(value == null ? '' : value).trim().replace(',', '.'));
}

function isTruthy(value) {
  const n = norm(value);
  if (n === '') return null;
  return ['oui', 'yes', 'true', 'vrai', '1', 'x', 'ok', 'verifie', 'verifiee'].includes(n);
}

/**
 * Colonnes reconnues. Plusieurs intitulés par champ : la feuille peut être
 * remplie à la main, on ne veut pas qu'une majuscule casse l'import.
 */
const ALIASES = {
  id: ['id', 'identifiant', 'ref', 'reference', 'no', 'numero'],
  produit: ['produit', 'product', 'handle', 'produit handle', 'article', 'sku'],
  statut: ['statut', 'status', 'etat', 'publication', 'publie'],
  note: ['note', 'rating', 'etoiles', 'stars', 'score', 'nombre d etoiles'],
  titre: ['titre', 'title', 'accroche', 'headline', 'objet'],
  avis: ['avis', 'review', 'texte', 'commentaire', 'contenu', 'body', 'message', 'temoignage'],
  auteur: ['auteur', 'author', 'nom', 'prenom', 'name', 'client', 'cliente', 'nom affiche'],
  age: ['age', 'tranche d age', 'tranche age', 'age range', 'classe d age'],
  verifie: ['verifie', 'verified', 'achat verifie', 'acheteur verifie', 'achat'],
  date: ['date', 'date avis', 'created at', 'date de publication', 'publie le'],
  nature: ['nature', 'nature de cheveux', 'nature des cheveux', 'type de cheveux', 'hair type'],
  preoccupation: [
    'preoccupation',
    'preoccupations',
    'preoccupation capillaire',
    'preoccupations capillaires',
    'concern',
    'probleme',
    'besoin',
  ],
  utile_oui: ['utile oui', 'helpful yes', 'pouce haut', 'utile', 'votes utiles'],
  utile_non: ['utile non', 'helpful no', 'pouce bas', 'pas utile', 'votes inutiles'],
  photos: ['photos', 'photo', 'images', 'image', 'url photo', 'url photos', 'lien photo'],
  epingle: ['epingle', 'epinglee', 'epingler', 'pin', 'pinned', 'a la une', 'mise en avant', 'prioritaire'],
};

function fieldForHeader(header) {
  const n = norm(header);
  if (!n) return null;
  for (const [field, aliases] of Object.entries(ALIASES)) {
    if (aliases.includes(n)) return field;
  }
  // Repli : « Note (1-5) » doit encore tomber sur `note`.
  for (const [field, aliases] of Object.entries(ALIASES)) {
    if (aliases.some((alias) => n === alias || n.startsWith(alias + ' '))) return field;
  }
  return null;
}

/* ------------------------------------------------------------- lecture brute */

function parseGviz(text) {
  const start = text.indexOf('(');
  const end = text.lastIndexOf(')');
  if (start === -1 || end === -1) throw new Error('Réponse gviz illisible');
  const payload = JSON.parse(text.slice(start + 1, end));
  const table = payload.table || {};
  const cols = table.cols || [];

  // Une feuille sans ligne d'en-tête déclarée renvoie « A », « B »… dans
  // `label` : dans ce cas la première ligne de données fait office d'en-tête.
  let headers = cols.map((col) => (col.label || '').trim());
  let rows = table.rows || [];
  const looksEmpty = headers.every((h) => h === '');
  if (looksEmpty && rows.length) {
    headers = (rows[0].c || []).map((cell) => (cell && (cell.f || cell.v)) || '');
    rows = rows.slice(1);
  }

  return rows.map((row) =>
    (row.c || []).map((cell) => {
      if (!cell) return '';
      // Les dates arrivent en `Date(2026,0,5)` ; la valeur formatée est plus sûre.
      if (typeof cell.v === 'string' && /^Date\(/.test(cell.v)) return cell.f || cell.v;
      if (cell.v instanceof Date) return cell.f || '';
      return cell.f != null && cell.f !== '' ? cell.f : cell.v == null ? '' : cell.v;
    })
  ).map((values) => rowToObject(headers, values));
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ',' || char === ';') {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (char !== '\r') {
      field += char;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  if (!rows.length) return [];

  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((values) => rowToObject(headers, values));
}

function rowToObject(headers, values) {
  const out = { _extra: {} };
  headers.forEach((header, index) => {
    const raw = values[index];
    const value = raw == null ? '' : String(raw).trim();
    const field = fieldForHeader(header);
    if (field) {
      if (out[field] == null || out[field] === '') out[field] = value;
    } else if (header) {
      out._extra[header] = value;
    }
  });
  return out;
}

function parseAny(text) {
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    const data = JSON.parse(trimmed);
    const list = Array.isArray(data) ? data : data.avis || data.reviews || data.rows || [];
    return list.map((item) => {
      const headers = Object.keys(item);
      return rowToObject(headers, headers.map((k) => item[k]));
    });
  }
  if (trimmed.startsWith('/*') || trimmed.includes('google.visualization.Query.setResponse')) {
    return parseGviz(trimmed);
  }
  return parseCsv(trimmed);
}

/* ------------------------------------------------------------ normalisation */

const HIDDEN_STATUS = ['masque', 'masquee', 'brouillon', 'en attente', 'attente', 'refuse', 'refusee', 'non', 'no', 'false', '0', 'supprime'];

function parseDate(value) {
  if (!value) return null;
  const raw = String(value).trim();

  // 05/01/26, 05/01/2026, 05-01-2026 → jour/mois/année (usage francophone).
  const fr = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (fr) {
    let year = Number(fr[3]);
    if (year < 100) year += 2000;
    const date = new Date(year, Number(fr[2]) - 1, Number(fr[1]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  // 2026-01-05
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDate(date, raw) {
  if (!date) return raw ? String(raw) : '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

/**
 * Une photo décrite par sa seule adresse. Sur le CDN Shopify on peut demander
 * une largeur ; ailleurs on prend le fichier tel quel. Les dimensions restent
 * inconnues, la carte s'en passe plutôt que d'inventer un format.
 */
function photoFromUrl(url) {
  const propre = String(url || '').trim();
  if (!propre) return null;
  const shopify = /cdn\.shopify\.com|\/cdn\/shop\//.test(propre);
  const dimensionne = (largeur) => {
    if (!shopify) return propre;
    return propre + (propre.includes('?') ? '&' : '?') + 'width=' + largeur;
  };
  return { url: dimensionne(1400), thumb: dimensionne(500), alt: '' };
}

function splitList(value) {
  return String(value || '')
    .split(/[,;|/]+|\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Comme `splitList`, mais sans la barre oblique : une adresse en est pleine. */
function splitUrls(value) {
  return String(value || '')
    .split(/[,;|]+|\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/* --------------------------------------------------------------- l'élément */

class DoseAvis extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.config = {
      dataUrl: this.getAttribute('data-url') || '',
      sheetId: this.getAttribute('sheet-id') || '',
      sheetName: this.getAttribute('sheet-name') || '',
      endpoint: this.getAttribute('endpoint') || '',
      product: this.getAttribute('product-handle') || '',
      productTitle: this.getAttribute('product-title') || '',
      perPage: Math.max(1, Number(this.getAttribute('per-page')) || 6),
      cacheMinutes: Number(this.getAttribute('cache-minutes')) || 0,
      manualScore: this.getAttribute('manual-score') || '',
      manualCount: this.getAttribute('manual-count') || '',
      separator: this.getAttribute('separator') || ',',
      scoreSuffix: this.getAttribute('score-suffix') || '',
      introTemplate: this.getAttribute('intro-template') || '',
      countTemplate: this.getAttribute('count-template') || '',
      verifiedLabel: this.getAttribute('verified-label') || 'Vérifié',
      helpfulLabel: this.getAttribute('helpful-label') || 'Utile ?',
      showHelpful: this.getAttribute('show-helpful') === 'true',
      emptyText: this.getAttribute('empty-text') || '',
      onlyPresent: this.getAttribute('only-present') === 'true',
      designMode: window.Shopify && window.Shopify.designMode,
    };

    this.state = {
      reviews: [],
      visible: [],
      page: 1,
      filters: { nature: '', preoccupation: '', note: '', photo: '' },
    };

    this.refs = {
      score: this.querySelector('[data-avis-score]'),
      stars: this.querySelector('[data-avis-stars]'),
      intro: this.querySelector('[data-avis-intro]'),
      count: this.querySelector('[data-avis-count]'),
      gallery: this.querySelector('[data-avis-gallery]'),
      galleryStrip: this.querySelector('[data-avis-gallery-strip]'),
      filters: this.querySelector('[data-avis-filters]'),
      list: this.querySelector('[data-avis-list]'),
      pagination: this.querySelector('[data-avis-pagination]'),
      status: this.querySelector('[data-avis-status]'),
      // `{% form %}` ne pose pas d'attribut personnalisé : on vise la classe.
      form: this.querySelector('.dose-avis__form'),
      lightbox: this.querySelector('[data-avis-lightbox]'),
    };

    this.votes = this.readVotes();
    this.bindFilters();
    this.bindAnchors();
    this.bindForm();
    this.bindLightbox();
    this.load();
  }

  /* ---------------------------------------------------------------- données */

  overrides() {
    return [...this.querySelectorAll('[data-avis-override]')]
      .map((node) => {
        try {
          return JSON.parse(node.textContent || 'null');
        } catch (error) {
          console.warn('[dose-avis] Bloc du thème illisible', error);
          return null;
        }
      })
      .filter(Boolean);
  }

  sourceUrl() {
    if (this.config.dataUrl) return this.config.dataUrl;
    if (!this.config.sheetId) return '';
    const sheet = this.config.sheetName ? `&sheet=${encodeURIComponent(this.config.sheetName)}` : '';
    return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(
      this.config.sheetId
    )}/gviz/tq?tqx=out:json&headers=1${sheet}`;
  }

  async fetchRows(url) {
    const cacheKey = CACHE_PREFIX + url;
    const ttl = this.config.cacheMinutes * 60 * 1000;

    if (ttl > 0 && !this.config.designMode) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.at < ttl) return cached.rows;
      } catch (error) {
        /* cache illisible : on refait l'appel, sans bruit */
      }
    }

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const rows = parseAny(await response.text());

    if (ttl > 0) {
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), rows }));
      } catch (error) {
        /* quota plein : le cache est un confort, pas une dépendance */
      }
    }
    return rows;
  }

  async load() {
    const url = this.sourceUrl();
    let rows = [];

    if (url) {
      try {
        rows = await this.fetchRows(url);
      } catch (error) {
        console.warn('[dose-avis] Google Sheet injoignable', error);
        this.showStatus(
          'Les avis du Google Sheet n’ont pas pu être chargés. Vérifiez que la feuille est partagée en lecture (« Tous les utilisateurs disposant du lien ») et que l’ID est correct.'
        );
      }
    } else if (this.config.designMode) {
      this.showStatus(
        'Aucune source renseignée : collez l’ID de votre Google Sheet dans les réglages de la section pour afficher les avis importés.'
      );
    }

    this.state.reviews = this.merge(rows, this.overrides());

    // Une feuille branchée mais vide donne une section muette : dans l'éditeur
    // de thème, le dire vaut mieux que laisser deviner.
    if (url && !rows.length && this.config.designMode && this.refs.status.hidden) {
      this.showStatus(
        'La feuille est bien lue, mais elle ne contient aucune ligne. Ajoutez un avis sous la ligne d’en-tête : il apparaîtra ici.'
      );
    }

    this.buildFilterOptions();
    this.apply();
  }

  /**
   * Fusionne la feuille (le texte) et les blocs du thème (les photos, les
   * retouches). Le lien se fait sur le prénom affiché — la seule clé que
   * l'on écrit déjà — et sur la date quand deux avis partagent un prénom.
   */
  merge(rows, overrides) {
    const reviews = [];
    rows.forEach((row, index) => {
      const review = this.normalizeRow(row, index);
      if (review) reviews.push(review);
    });

    const orphelins = [];

    overrides.forEach((block, index) => {
      const cibles = this.findTargets(reviews, block);

      if (cibles.length) {
        if (cibles.length > 1) {
          orphelins.push(
            `« ${block.auteur_cible} » désigne ${cibles.length} avis : précisez la date dans le bloc pour lever l’ambiguïté.`
          );
        }
        const target = cibles[0];
        // Le bloc ne remplace que ce qu'il renseigne : un champ laissé vide
        // dans le thème laisse parler la feuille.
        ['titre', 'avis', 'age', 'nature', 'preoccupation'].forEach((field) => {
          if (block[field]) target[field] = block[field];
        });
        if (block.note) target.note = Math.max(1, Math.min(5, Number(block.note)));
        if (block.verifie) target.verifie = isTruthy(block.verifie);
        if (block.epingler) target.epingle = true;
        if (block.photos && block.photos.length) target.photos = block.photos;
        target.blockId = block.blockId;
        if (block.masquer) target.hidden = true;
        return;
      }

      if (block.auteur_cible) {
        orphelins.push(
          `Aucun avis au nom de « ${block.auteur_cible} »${
            block.date_cible ? ` daté du ${block.date_cible}` : ''
          } : vérifiez l’orthographe du prénom dans la feuille.`
        );
      }

      // Pas de correspondance : le bloc vaut avis à lui seul, à condition de
      // porter un texte.
      if (!block.avis && !block.titre) return;
      const standalone = this.normalizeRow(
        {
          note: block.note,
          titre: block.titre,
          avis: block.avis,
          auteur: block.auteur_cible,
          age: block.age,
          verifie: block.verifie || 'oui',
          date: block.date_cible,
          nature: block.nature,
          preoccupation: block.preoccupation,
        },
        1000 + index
      );
      if (!standalone) return;
      standalone.photos = block.photos || [];
      standalone.blockId = block.blockId;
      if (block.masquer) standalone.hidden = true;
      reviews.push(standalone);
    });

    if (orphelins.length && this.config.designMode) {
      this.showStatus(orphelins.join(' '));
    }

    const kept = reviews.filter((review) => !review.hidden);
    kept.sort((a, b) => {
      // Les avis épinglés passent devant : ils ouvrent la première page.
      if (a.epingle !== b.epingle) return a.epingle ? -1 : 1;
      if (a.dateObj && b.dateObj) return b.dateObj - a.dateObj;
      if (a.dateObj) return -1;
      if (b.dateObj) return 1;
      return a.order - b.order;
    });
    return kept;
  }

  /** Les avis que ce bloc désigne : par prénom, affiné par la date si besoin. */
  findTargets(reviews, block) {
    const auteur = norm(block.auteur_cible);
    if (!auteur) return [];

    const attendue = parseDate(block.date_cible);
    return reviews.filter((review) => {
      if (norm(review.auteur) !== auteur) return false;
      if (!block.date_cible) return true;
      if (attendue && review.dateObj) return attendue.getTime() === review.dateObj.getTime();
      return norm(review.date) === norm(block.date_cible);
    });
  }

  normalizeRow(row, order) {
    const statut = norm(row.statut);
    if (statut && HIDDEN_STATUS.includes(statut)) return null;

    const texte = (row.avis || '').trim();
    const titre = (row.titre || '').trim();
    if (!texte && !titre) return null;

    // Un avis peut viser un produit précis, plusieurs, ou tous.
    const cible = norm(row.produit);
    if (cible && !['tous', 'toutes', 'all', 'tout'].includes(cible)) {
      const cibles = splitList(row.produit).map(norm);
      const moi = [norm(this.config.product), norm(this.config.productTitle)].filter(Boolean);
      if (!cibles.some((c) => moi.includes(c))) return null;
    }

    const dateObj = parseDate(row.date);
    const verifie = isTruthy(row.verifie);

    return {
      id: (row.id || '').trim() || fingerprint(row),
      order,
      note: Math.max(1, Math.min(5, Math.round(Number(row.note) || 5))),
      titre,
      avis: texte,
      auteur: (row.auteur || '').trim(),
      age: (row.age || '').trim(),
      verifie: verifie == null ? true : verifie,
      date: row.date || '',
      dateObj,
      dateLabel: formatDate(dateObj, row.date),
      nature: (row.nature || '').trim(),
      preoccupation: (row.preoccupation || '').trim(),
      utileOui: Number(row.utile_oui) || 0,
      utileNon: Number(row.utile_non) || 0,
      photos: splitUrls(row.photos).map(photoFromUrl).filter(Boolean),
      epingle: isTruthy(row.epingle) === true,
      hidden: false,
    };
  }

  /* --------------------------------------------------------------- filtres */

  buildFilterOptions() {
    if (!this.refs.filters) return;

    this.refs.filters.querySelectorAll('[data-avis-filter]').forEach((dropdown) => {
      const key = dropdown.getAttribute('data-avis-filter');
      if (key !== 'nature' && key !== 'preoccupation') return;

      const menu = dropdown.querySelector('[data-avis-filter-menu]');
      if (!menu) return;

      const declared = Array.from(menu.querySelectorAll('[data-value]'))
        .map((item) => item.getAttribute('data-value'))
        .filter(Boolean);

      const present = new Set();
      this.state.reviews.forEach((review) => {
        splitList(review[key]).forEach((value) => present.add(value));
      });

      const ordered = [];
      declared.forEach((value) => {
        if (!this.config.onlyPresent || [...present].some((p) => norm(p) === norm(value))) {
          ordered.push(value);
        }
      });
      [...present].forEach((value) => {
        if (!ordered.some((v) => norm(v) === norm(value))) ordered.push(value);
      });

      const all = menu.querySelector('[data-value=""]');
      menu.innerHTML = '';
      if (all) menu.appendChild(all);
      ordered.forEach((value) => {
        menu.appendChild(this.filterOption(value, value));
      });
    });
  }

  filterOption(value, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dose-avis__option';
    button.setAttribute('data-value', value);
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', 'false');
    button.innerHTML = `<span>${escapeHtml(label)}</span>${this.checkIcon()}`;
    return button;
  }

  checkIcon() {
    return '<svg class="dose-avis__check" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 8.5l3 3 6-6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  bindFilters() {
    if (!this.refs.filters) return;

    this.refs.filters.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-avis-filter-trigger]');
      if (trigger) {
        const dropdown = trigger.closest('[data-avis-filter]');
        const wasOpen = dropdown.hasAttribute('data-open');
        this.closeFilters();
        if (!wasOpen) {
          dropdown.setAttribute('data-open', '');
          trigger.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      const option = event.target.closest('[data-value]');
      if (!option) return;
      const dropdown = option.closest('[data-avis-filter]');
      const key = dropdown.getAttribute('data-avis-filter');
      const value = option.getAttribute('data-value');

      this.state.filters[key] = value;
      this.state.page = 1;

      const label = dropdown.querySelector('[data-avis-filter-label]');
      const base = dropdown.getAttribute('data-label');
      if (label) label.textContent = value ? option.textContent.trim() : base;
      dropdown.classList.toggle('is-active', Boolean(value));

      dropdown.querySelectorAll('[data-value]').forEach((item) => {
        item.setAttribute('aria-selected', item === option ? 'true' : 'false');
      });

      this.closeFilters();
      this.apply();
    });

    document.addEventListener('click', (event) => {
      if (!this.refs.filters.contains(event.target)) this.closeFilters();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeFilters();
    });
  }

  closeFilters() {
    this.refs.filters.querySelectorAll('[data-avis-filter]').forEach((dropdown) => {
      dropdown.removeAttribute('data-open');
      const trigger = dropdown.querySelector('[data-avis-filter-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  matches(review) {
    const { nature, preoccupation, note, photo } = this.state.filters;
    if (nature && !splitList(review.nature).some((v) => norm(v) === norm(nature))) return false;
    if (preoccupation && !splitList(review.preoccupation).some((v) => norm(v) === norm(preoccupation))) return false;
    if (note && review.note !== Number(note)) return false;
    if (photo === 'avec' && !review.photos.length) return false;
    if (photo === 'sans' && review.photos.length) return false;
    return true;
  }

  apply() {
    this.state.visible = this.state.reviews.filter((review) => this.matches(review));
    const pages = Math.max(1, Math.ceil(this.state.visible.length / this.config.perPage));
    if (this.state.page > pages) this.state.page = pages;
    this.renderHeader();
    this.renderGallery();
    this.renderList();
    this.renderPagination(pages);
  }

  /* ---------------------------------------------------------------- rendus */

  starsMarkup(note, size) {
    const filled = Math.round(note);
    let html = `<span class="dose-avis__stars-row" style="--star-size:${size}px" role="img" aria-label="${note} étoiles sur 5">`;
    for (let i = 1; i <= 5; i += 1) {
      html += `<svg class="dose-avis__star${i <= filled ? '' : ' is-empty'}" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 1.3l2.06 4.18 4.61.67-3.34 3.25.79 4.6L8 11.83l-4.12 2.17.79-4.6L1.33 6.15l4.61-.67z"/></svg>`;
    }
    return `${html}</span>`;
  }

  renderHeader() {
    const total = this.state.reviews.length;
    const moyenne = total
      ? this.state.reviews.reduce((sum, review) => sum + review.note, 0) / total
      : 0;

    const note = this.config.manualScore || (total ? moyenne.toFixed(1) : '');
    const nombre = this.config.manualCount || (total ? String(total) : '');
    const noteAffichee = String(note).replace('.', this.config.separator);

    if (this.refs.score) {
      this.refs.score.textContent = note
        ? `${noteAffichee}/5${this.config.scoreSuffix ? ' ' + this.config.scoreSuffix : ''}`
        : '';
    }
    if (this.refs.stars) {
      this.refs.stars.innerHTML = note ? this.starsMarkup(toNote(note) || 5, 16) : '';
    }
    // Le nombre total vit sur sa propre ligne : on peut l'afficher sans être
    // obligé de le glisser dans la phrase, ou l'inverse.
    if (this.refs.count) {
      this.refs.count.textContent = nombre
        ? (this.config.countTemplate || '{nombre}').replace(/\{nombre\}/g, nombre)
        : '';
    }

    this.syncExternal(note, nombre);

    if (this.refs.intro && this.config.introTemplate) {
      this.refs.intro.innerHTML = note
        ? this.config.introTemplate.replace(/\{note\}/g, noteAffichee).replace(/\{nombre\}/g, nombre)
        : '';
    }
  }

  /**
   * Réécrit les blocs d'étoiles qui se sont déclarés reliés à cette section :
   * la note du haut de fiche et celle du bas ne peuvent pas diverger.
   */
  syncExternal(note, nombre) {
    const cibles = document.querySelectorAll('[data-dose-avis-sync]');
    if (!cibles.length || !note) return;

    const valeur = toNote(note);
    // Une note illisible ne doit pas effacer les étoiles rendues par le thème.
    if (!Number.isFinite(valeur)) return;
    const pleines = Math.floor(valeur);
    const reste = valeur - pleines;

    cibles.forEach((cible) => {
      const rangee = cible.querySelector('[data-rating-stars]');
      const gabarit = (nom) => {
        const t = cible.querySelector(`template[data-dose-star="${nom}"]`);
        return t ? t.innerHTML : '';
      };

      // Même règle que le thème : la moindre décimale donne une demi-étoile.
      if (rangee && gabarit('full')) {
        let html = '';
        for (let i = 1; i <= 5; i += 1) {
          if (i <= pleines) html += gabarit('full');
          else if (i === pleines + 1 && reste > 0) html += gabarit('half');
          else html += gabarit('empty');
        }
        rangee.innerHTML = html;
      }

      const affichee = cible.querySelector('[data-rating-note]');
      if (affichee) affichee.textContent = `${String(note).replace('.', this.config.separator)}/5`;

      const compte = cible.querySelector('[data-rating-count]');
      if (compte && nombre) compte.textContent = String(nombre);

      cible.setAttribute('aria-label', `${note} sur 5 — voir les avis`);
    });
  }

  /**
   * Le lien du haut de fiche descend en douceur plutôt que de sauter.
   * Si le défilement doux ne prend pas — mouvement réduit, navigateur qui
   * l'ignore, page dont le défilement est confié à un conteneur — on repasse
   * la main à l'ancre. Mieux vaut un saut sec qu'un lien mort.
   */
  bindAnchors() {
    document.addEventListener('click', (event) => {
      const lien = event.target.closest('[data-dose-avis-sync]');
      if (!lien) return;
      const href = lien.getAttribute('href') || '';
      if (!href.startsWith('#')) return;

      const cible = document.querySelector(href);
      if (cible !== this && !this.contains(cible)) return;

      const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const avant = window.scrollY;
      event.preventDefault();
      this.scrollIntoView({ behavior: doux ? 'smooth' : 'auto', block: 'start' });

      window.setTimeout(() => {
        if (Math.abs(window.scrollY - avant) < 4) window.location.hash = href;
      }, 400);
    });
  }

  allPhotos() {
    const photos = [];
    this.state.reviews.forEach((review) => {
      (review.photos || []).forEach((photo, index) => {
        photos.push({ ...photo, review, index });
      });
    });
    return photos;
  }

  renderGallery() {
    if (!this.refs.gallery || !this.refs.galleryStrip) return;
    const photos = this.allPhotos();
    this.galleryPhotos = photos;

    if (!photos.length) {
      this.refs.gallery.hidden = true;
      return;
    }
    this.refs.gallery.hidden = false;
    this.refs.galleryStrip.innerHTML = photos
      .map(
        (photo, index) => `
        <button type="button" class="dose-avis__thumb" data-avis-gallery-item="${index}" aria-label="Ouvrir la photo de ${escapeHtml(
          photo.review.auteur || 'cliente'
        )}">
          <img src="${escapeHtml(photo.thumb || photo.url)}" alt="${escapeHtml(photo.alt || '')}" loading="lazy"${
          photo.width && photo.height ? ` width="${photo.width}" height="${photo.height}"` : ''
        }>
        </button>`
      )
      .join('');
  }

  renderList() {
    if (!this.refs.list) return;
    const start = (this.state.page - 1) * this.config.perPage;
    const page = this.state.visible.slice(start, start + this.config.perPage);

    if (!page.length) {
      this.refs.list.innerHTML = `<p class="dose-avis__empty">${escapeHtml(this.config.emptyText)}</p>`;
      return;
    }

    this.refs.list.innerHTML = page.map((review) => this.cardMarkup(review)).join('');
  }

  cardMarkup(review) {
    const meta = [];
    if (review.age) meta.push(`<span class="dose-avis__age">${escapeHtml(review.age)}</span>`);
    if (review.dateLabel) meta.push(`<span class="dose-avis__date">${escapeHtml(review.dateLabel)}</span>`);

    const photos = (review.photos || [])
      .map(
        (photo, index) => `
        <button type="button" class="dose-avis__photo" data-avis-photo="${escapeHtml(review.id)}" data-avis-photo-index="${index}">
          <img src="${escapeHtml(photo.url)}" alt="${escapeHtml(photo.alt || '')}" loading="lazy"${
          photo.width && photo.height ? ` width="${photo.width}" height="${photo.height}"` : ''
        }>
        </button>`
      )
      .join('');

    const vote = this.votes[review.id];
    const oui = review.utileOui + (vote === 'up' ? 1 : 0);
    const non = review.utileNon + (vote === 'down' ? 1 : 0);

    const helpful = this.config.showHelpful
      ? `
      <div class="dose-avis__helpful">
        <span class="dose-avis__helpful-label">${escapeHtml(this.config.helpfulLabel)}</span>
        <button type="button" class="dose-avis__vote${vote === 'up' ? ' is-voted' : ''}" data-avis-vote="up" data-avis-id="${escapeHtml(
          review.id
        )}" aria-pressed="${vote === 'up'}" aria-label="Cet avis m’a été utile">
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M6.2 17.5V8.6l4-6a1.6 1.6 0 0 1 2.9 1.1l-.7 3.6h4a1.6 1.6 0 0 1 1.6 1.9l-1.1 6a1.6 1.6 0 0 1-1.6 1.3H6.2Zm0 0H2.5V8.6h3.7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
          <span data-avis-count>(${oui})</span>
        </button>
        <button type="button" class="dose-avis__vote${vote === 'down' ? ' is-voted' : ''}" data-avis-vote="down" data-avis-id="${escapeHtml(
          review.id
        )}" aria-pressed="${vote === 'down'}" aria-label="Cet avis ne m’a pas été utile">
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M13.8 2.5v8.9l-4 6a1.6 1.6 0 0 1-2.9-1.1l.7-3.6h-4a1.6 1.6 0 0 1-1.6-1.9l1.1-6A1.6 1.6 0 0 1 4.7 2.5h9.1Zm0 0h3.7v8.9h-3.7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
          <span data-avis-count>(${non})</span>
        </button>
      </div>`
      : '';

    return `
    <article class="dose-avis__card" data-avis-card="${escapeHtml(review.id)}">
      <div class="dose-avis__card-head">
        <div class="dose-avis__card-left">
          ${this.starsMarkup(review.note, 13)}
          ${review.titre ? `<h3 class="dose-avis__card-title">${escapeHtml(review.titre)}</h3>` : ''}
        </div>
        <div class="dose-avis__card-right">
          <p class="dose-avis__author">
            ${review.auteur ? `<span class="dose-avis__name">${escapeHtml(review.auteur)}</span>` : ''}
            ${review.verifie ? `<span class="dose-avis__verified">${escapeHtml(this.config.verifiedLabel)}</span>` : ''}
          </p>
          ${meta.length ? `<p class="dose-avis__meta">${meta.join('')}</p>` : ''}
        </div>
      </div>
      ${review.avis ? `<div class="dose-avis__body">${textToHtml(review.avis)}</div>` : ''}
      ${photos ? `<div class="dose-avis__photos">${photos}</div>` : ''}
      ${helpful}
    </article>`;
  }

  renderPagination(pages) {
    if (!this.refs.pagination) return;
    if (pages <= 1) {
      this.refs.pagination.innerHTML = '';
      this.refs.pagination.hidden = true;
      return;
    }
    this.refs.pagination.hidden = false;

    // Fenêtre glissante : au plus sept numéros, la page courante au centre.
    const max = 7;
    let start = Math.max(1, this.state.page - Math.floor(max / 2));
    const end = Math.min(pages, start + max - 1);
    start = Math.max(1, end - max + 1);

    let html = '';
    for (let page = start; page <= end; page += 1) {
      const current = page === this.state.page;
      html += `<button type="button" class="dose-avis__page${current ? ' is-current' : ''}" data-avis-page="${page}"${
        current ? ' aria-current="page"' : ''
      }>${String(page).padStart(2, '0')}</button>`;
    }
    this.refs.pagination.innerHTML = html;
  }

  showStatus(message) {
    if (!this.refs.status) return;
    this.refs.status.textContent = message;
    this.refs.status.hidden = false;
  }

  /* ----------------------------------------------------------------- votes */

  readVotes() {
    try {
      return JSON.parse(localStorage.getItem(VOTE_STORE) || '{}');
    } catch (error) {
      return {};
    }
  }

  saveVotes() {
    try {
      localStorage.setItem(VOTE_STORE, JSON.stringify(this.votes));
    } catch (error) {
      /* navigation privée : le vote ne survivra pas à la page, tant pis */
    }
  }

  vote(id, value) {
    const avis = this.state.reviews.find((review) => review.id === id);
    const current = this.votes[id];
    if (current === value) delete this.votes[id];
    else this.votes[id] = value;
    this.saveVotes();
    this.renderList();

    if (this.config.endpoint) {
      // `URLSearchParams` et non `FormData` : Apps Script ne remplit `e.parameter`
      // que pour un corps encodé en formulaire, jamais pour du multipart.
      const body = new URLSearchParams();
      body.append('action', 'vote');
      body.append('id', id);
      body.append('valeur', this.votes[id] || '');
      body.append('precedent', current || '');
      // Sans colonne « id », c'est le couple prénom + date qui retrouve la ligne.
      body.append('auteur', (avis && avis.auteur) || '');
      body.append('date', (avis && (avis.date || avis.dateLabel)) || '');
      fetch(this.config.endpoint, { method: 'POST', mode: 'no-cors', body }).catch(() => {});
    }
  }

  /* -------------------------------------------------------------- lightbox */

  bindLightbox() {
    this.addEventListener('click', (event) => {
      const page = event.target.closest('[data-avis-page]');
      if (page) {
        this.state.page = Number(page.getAttribute('data-avis-page'));
        this.renderList();
        this.renderPagination(Math.max(1, Math.ceil(this.state.visible.length / this.config.perPage)));
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const voteButton = event.target.closest('[data-avis-vote]');
      if (voteButton) {
        this.vote(voteButton.getAttribute('data-avis-id'), voteButton.getAttribute('data-avis-vote'));
        return;
      }

      const thumb = event.target.closest('[data-avis-gallery-item]');
      if (thumb) {
        this.openLightbox(this.galleryPhotos, Number(thumb.getAttribute('data-avis-gallery-item')));
        return;
      }

      const photo = event.target.closest('[data-avis-photo]');
      if (photo) {
        const id = photo.getAttribute('data-avis-photo');
        const index = Number(photo.getAttribute('data-avis-photo-index'));
        const review = this.state.reviews.find((item) => item.id === id);
        if (!review) return;
        const list = review.photos.map((item, i) => ({ ...item, review, index: i }));
        this.openLightbox(list, index);
      }
    });

    if (!this.refs.lightbox) return;

    this.refs.lightbox.addEventListener('click', (event) => {
      if (event.target.closest('[data-avis-close]') || event.target === this.refs.lightbox) {
        this.closeLightbox();
        return;
      }
      const step = event.target.closest('[data-avis-step]');
      if (step) this.stepLightbox(Number(step.getAttribute('data-avis-step')));
    });

    document.addEventListener('keydown', (event) => {
      if (this.refs.lightbox.hidden) return;
      if (event.key === 'Escape') this.closeLightbox();
      if (event.key === 'ArrowRight') this.stepLightbox(1);
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
    });
  }

  openLightbox(photos, index) {
    if (!this.refs.lightbox || !photos || !photos.length) return;
    this.lightboxPhotos = photos;
    this.lightboxIndex = index;
    this.refs.lightbox.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.paintLightbox();
  }

  closeLightbox() {
    if (!this.refs.lightbox) return;
    this.refs.lightbox.hidden = true;
    document.documentElement.style.overflow = '';
  }

  stepLightbox(delta) {
    if (!this.lightboxPhotos) return;
    const count = this.lightboxPhotos.length;
    this.lightboxIndex = (this.lightboxIndex + delta + count) % count;
    this.paintLightbox();
  }

  paintLightbox() {
    const photo = this.lightboxPhotos[this.lightboxIndex];
    const review = photo.review;
    const image = this.refs.lightbox.querySelector('[data-avis-lightbox-image]');
    const caption = this.refs.lightbox.querySelector('[data-avis-lightbox-caption]');
    const nav = this.refs.lightbox.querySelectorAll('[data-avis-step]');

    if (image) {
      image.src = photo.url;
      image.alt = photo.alt || '';
    }
    if (caption) {
      caption.innerHTML = `
        ${this.starsMarkup(review.note, 13)}
        ${review.titre ? `<p class="dose-avis__lightbox-title">${escapeHtml(review.titre)}</p>` : ''}
        ${review.avis ? `<p class="dose-avis__lightbox-text">${textToHtml(review.avis)}</p>` : ''}
        <p class="dose-avis__lightbox-author"><span>${escapeHtml(review.auteur)}</span>${
        review.verifie
          ? `<span class="dose-avis__verified">${escapeHtml(this.config.verifiedLabel)}</span>`
          : ''
      }</p>`;
    }
    nav.forEach((button) => {
      button.hidden = this.lightboxPhotos.length < 2;
    });
  }

  /* ------------------------------------------------------------ formulaire */

  bindForm() {
    const form = this.refs.form;
    if (!form) return;

    const stars = form.querySelectorAll('[data-avis-star]');
    const noteInput = form.querySelector('[data-avis-note-input]');
    const rest = form.querySelector('[data-avis-form-rest]');

    const paint = (value) => {
      stars.forEach((star) => {
        const own = Number(star.getAttribute('data-avis-star'));
        star.classList.toggle('is-on', own <= value);
        star.setAttribute('aria-checked', own === value ? 'true' : 'false');
      });
    };

    stars.forEach((star) => {
      star.addEventListener('click', () => {
        const value = Number(star.getAttribute('data-avis-star'));
        if (noteInput) noteInput.value = String(value);
        paint(value);
        if (rest) rest.hidden = false;
      });
      star.addEventListener('mouseenter', () => paint(Number(star.getAttribute('data-avis-star'))));
    });
    form.addEventListener('mouseleave', () => paint(Number(noteInput && noteInput.value) || 0));

    // Sans script Google configuré, on laisse le formulaire Shopify partir
    // normalement : la boutique reçoit l'avis par e-mail comme un message.
    if (!this.config.endpoint) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const body = new URLSearchParams(new FormData(form));
      body.append('action', 'avis');
      body.append('produit', this.config.product);
      body.append('produit_titre', this.config.productTitle);
      body.append('page', window.location.href);

      const button = form.querySelector('[type="submit"]');
      if (button) {
        button.disabled = true;
        button.dataset.label = button.textContent;
        button.textContent = 'Envoi…';
      }

      fetch(this.config.endpoint, { method: 'POST', mode: 'no-cors', body })
        .catch(() => {})
        .finally(() => {
          const success = this.querySelector('[data-avis-form-success]');
          form.hidden = true;
          if (success) success.hidden = false;
        });
    });
  }
}

if (!customElements.get('dose-avis')) customElements.define('dose-avis', DoseAvis);
